import React, { useState, useEffect, useRef } from 'react';
import CustomerModal from './CustomerModal';
import PaymentModal from './PaymentModal';
import PhotoEditorModal from './PhotoEditorModal';
import RepairModal from './RepairModal';
import AppraisalModal from './AppraisalModal';
import CustomJobModal from './CustomJobModal';
import { useShop, useAuth } from '../App';
import { formatTicketId } from '../utils/ticketId';
import { toJpegOrPng } from '../utils/imageUpload';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function TransactionModal({ isOpen, onClose, customer, editingTransaction, onSuccess }) {
  const { shops, currentShop } = useShop();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [lineItems, setLineItems] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState(customer ? [customer] : (editingTransaction?.customer ? [editingTransaction.customer] : []));
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  // Payments state
  const [payments, setPayments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showLineItemModal, setShowLineItemModal] = useState(false);
  // Repair and Appraisal modal states for creating new records from transaction
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [showAppraisalModal, setShowAppraisalModal] = useState(false);
  const [showCustomJobModal, setShowCustomJobModal] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [isLayaway, setIsLayaway] = useState(false);
  const searchInputRef = useRef(null);
  const photoUploadRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Photo editor modal state
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [photoEditorItemIndex, setPhotoEditorItemIndex] = useState(-1);
  // Unattached repairs (ghost entries)
  const [unattachedRepairs, setUnattachedRepairs] = useState([]);
  const [showRepairsSection, setShowRepairsSection] = useState(false);
  const [selectedRepairs, setSelectedRepairs] = useState([]);
  // Unattached appraisals (ghost entries)
  const [unattachedAppraisals, setUnattachedAppraisals] = useState([]);
  const [showAppraisalsSection, setShowAppraisalsSection] = useState(false);
  const [selectedAppraisals, setSelectedAppraisals] = useState([]);
  // Unattached custom jobs (ghost entries)
  const [unattachedCustomJobs, setUnattachedCustomJobs] = useState([]);
  const [showCustomJobsSection, setShowCustomJobsSection] = useState(false);
  const [selectedCustomJobs, setSelectedCustomJobs] = useState([]);
  const createdTransactionIdRef = useRef(null);
  const [currentItem, setCurrentItem] = useState({
    description: '',
    quantity: 1,
    unitPrice: '',
    taxable: true,
    taxIncluded: false,
    photo_url: null,
    title: '',
    sku: ''
  });
  const [editingItemId, setEditingItemId] = useState(null);
  const [editValues, setEditValues] = useState({ description: '', quantity: 1, unitPrice: 0 });
  // Parent record (repair/custom job) being edited from a source line item
  const [editSourceRecord, setEditSourceRecord] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const descriptionInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  const unitPriceInputRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [shopId, setShopId] = useState('');
  // Dirty state tracking
  const [isDirty, setIsDirty] = useState(false);

  // Fetch unattached repairs - defined outside useEffect so it's accessible elsewhere
  const fetchUnattachedRepairs = async (customerId) => {
    try {
      const url = customerId
        ? `${API_URL}/repairs/unattached?customer_id=${customerId}`
        : `${API_URL}/repairs/unattached`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUnattachedRepairs(data.repairs || []);
      }
    } catch (err) {
      console.error('Error fetching unattached repairs:', err);
    }
  };

  // Fetch unattached appraisals - defined outside useEffect so it's accessible elsewhere
  const fetchUnattachedAppraisals = async (customerId) => {
    try {
      const url = customerId
        ? `${API_URL}/appraisals/unattached?customer_id=${customerId}`
        : `${API_URL}/appraisals/unattached`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUnattachedAppraisals(data.appraisals || []);
      }
    } catch (err) {
      console.error('Error fetching unattached appraisals:', err);
    }
  };

  // Fetch unattached custom jobs - defined outside useEffect so it's accessible elsewhere
  const fetchUnattachedCustomJobs = async (customerId) => {
    try {
      const url = customerId
        ? `${API_URL}/custom-jobs/unattached?customer_id=${customerId}`
        : `${API_URL}/custom-jobs/unattached`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUnattachedCustomJobs(data.customJobs || []);
      }
    } catch (err) {
      console.error('Error fetching unattached custom jobs:', err);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    const fetchTransactionDetails = async () => {
      if (editingTransaction && editingTransaction.id && !editingTransaction.line_items) {
        try {
          const response = await fetch(`${API_URL}/transactions/${editingTransaction.id}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setLineItems((data.line_items || []).map(item => ({
              ...item,
              unitPrice: item.unit_price,
              taxIncluded: item.tax_included || false,
              returned: item.returned || false,
              photos: typeof item.photos === 'string' ? JSON.parse(item.photos) : (Array.isArray(item.photos) ? item.photos : []),
              repair_id: item.repair_id || null,
              appraisal_id: item.appraisal_id || null,
              custom_job_id: item.custom_job_id || null,
              repair_instructions: item.repair_instructions || '',
              repair_due_type: item.repair_due_type || null,
              repair_due_date: item.repair_due_date || null,
              repair_created_by_name: item.repair_created_by_name || null,
              cj_instructions: item.cj_instructions || '',
              cj_due_type: item.cj_due_type || null,
              cj_due_date: item.cj_due_date || null,
              cj_created_by_name: item.cj_created_by_name || null
            })));
            setPayments(data.payments || []);
          }
        } catch (err) {
          console.error('Error fetching transaction details:', err);
        }
      } else {
        setLineItems(editingTransaction ? (editingTransaction.line_items || []).map(item => ({
          ...item,
          unitPrice: item.unit_price,
          taxIncluded: item.tax_included || false,
          returned: item.returned || false,
          photos: typeof item.photos === 'string' ? JSON.parse(item.photos) : (Array.isArray(item.photos) ? item.photos : []),
          repair_id: item.repair_id || null,
          appraisal_id: item.appraisal_id || null,
          custom_job_id: item.custom_job_id || null,
          repair_instructions: item.repair_instructions || '',
          repair_due_type: item.repair_due_type || null,
          repair_due_date: item.repair_due_date || null,
          repair_created_by_name: item.repair_created_by_name || null,
          cj_instructions: item.cj_instructions || '',
          cj_due_type: item.cj_due_type || null,
          cj_due_date: item.cj_due_date || null,
          cj_created_by_name: item.cj_created_by_name || null
        })) : []);
        setPayments(editingTransaction?.payments || []);
      }
    };

    if (isOpen) {
      setSelectedCustomers(customer ? [customer] : (editingTransaction?.customer ? [editingTransaction.customer] : []));
      fetchTransactionDetails();
      if (customer) {
        fetchUnattachedRepairs(customer.id);
        fetchUnattachedAppraisals(customer.id);
        fetchUnattachedCustomJobs(customer.id);
      } else {
        setUnattachedRepairs([]);
        setUnattachedAppraisals([]);
        setUnattachedCustomJobs([]);
      }
      setSearchQuery('');
      setSearchResults([]);
      setShowSearchResults(false);
      setShowCustomerModal(false);
      setShowPaymentModal(false);
      setEditingPayment(null);
      setCurrentItem({ description: '', quantity: 1, unitPrice: '', taxable: true, taxIncluded: false, photo_url: null, title: '', sku: '', photos: [] });
      setEditingItemId(null);
      setError(null);
      setSuccess(false);
      setIsClosed(editingTransaction?.is_closed || false);
      setIsLayaway(editingTransaction?.is_layaway || false);
      setShowRepairsSection(false);
      setShowAppraisalsSection(false);
      setShowCustomJobsSection(false);
      setShopId(editingTransaction?.shop_id || (currentShop?.shop_id || ''));
      setSelectedRepairs([]);
      setSelectedAppraisals([]);
      setSelectedCustomJobs([]);
      setShowRepairModal(false);
      setShowAppraisalModal(false);
      setShowCustomJobModal(false);
      setEditSourceRecord(null);
      createdTransactionIdRef.current = null;
      setIsDirty(false);
    }
  }, [isOpen, customer, editingTransaction]);

  // Refetch unattached repairs and appraisals when selected customers change
  useEffect(() => {
    if (selectedCustomers.length > 0) {
      fetchUnattachedRepairs(selectedCustomers[0].id);
      fetchUnattachedAppraisals(selectedCustomers[0].id);
      fetchUnattachedCustomJobs(selectedCustomers[0].id);
    } else {
      setUnattachedRepairs([]);
      setUnattachedAppraisals([]);
      setUnattachedCustomJobs([]);
    }
  }, [selectedCustomers]);

  // Fetch customers based on search query
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        const response = await fetch(`${API_URL}/customers/search/autocomplete?q=${encodeURIComponent(searchQuery)}&limit=10`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          const customers = (data.customers || []).slice(0, 10);
          setSearchResults(customers);
          setShowSearchResults(true);
        }
      } catch (err) {
        console.error('Error fetching customers:', err);
      } finally {
        setSearching(false);
      }
    };

    const debounceTimer = setTimeout(fetchCustomers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleCustomerSelect = (customer) => {
    if (selectedCustomers.some(c => c.id === customer.id)) return;
    setSelectedCustomers([...selectedCustomers, customer]);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setIsDirty(true);
  };

  const handleRemoveCustomer = (customerId) => {
    setSelectedCustomers(selectedCustomers.filter(c => c.id !== customerId));
    searchInputRef.current?.focus();
    setIsDirty(true);
  };

  const handleCreateNewCustomer = () => {
    setShowCustomerModal(true);
    setShowSearchResults(false);
  };

  const handleCustomerCreated = (newCustomer) => {
    setSelectedCustomers([...selectedCustomers, newCustomer]);
    setShowCustomerModal(false);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value.length >= 2) {
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  };

  
  const handleAddLineItem = () => {
      const newItem = {
        id: Date.now(),
        ...currentItem,
        description: `${currentItem.description || 'Untitled Item'}`.trim(),
        quantity: parseInt(currentItem.quantity) || 1,
        unitPrice: parseFloat(currentItem.unitPrice) || 0,
        taxable: currentItem.taxable,
        taxIncluded: currentItem.taxIncluded,
        photo_url: currentItem.photo_url,
        title: currentItem.title ? currentItem.title.substring(0, 100) : '',
        sku: currentItem.sku ? currentItem.sku.substring(0, 20) : ''
      };
      setLineItems([...lineItems, newItem]);
      setCurrentItem({ description: '', quantity: 1, unitPrice: '', taxable: true, taxIncluded: false, photo_url: null, title: '', sku: '', photos: [] });
      setIsDirty(true);
  };

  const handleToggleReturn = (id) => {
    // Pure local state toggle - persists on save, no API call
    setLineItems(items => items.map(i => i.id === id ? { ...i, returned: !i.returned } : i));
    setIsDirty(true);
  };

  const handleRemoveLineItem = async (id) => {
    const item = lineItems.find(i => i.id === id);
    // If the line item has a repair_id or appraisal_id, call the backend DELETE endpoint to detach
    if ((item?.repair_id || item?.appraisal_id || item?.custom_job_id) && editingTransaction?.id) {
      try {
        await fetch(`${API_URL}/transactions/${editingTransaction.id}/line-items/${id}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        // Refetch unattached repairs to show the detached repair
        if (selectedCustomers.length > 0) {
          fetchUnattachedRepairs(selectedCustomers[0].id);
          fetchUnattachedAppraisals(selectedCustomers[0].id);
          fetchUnattachedCustomJobs(selectedCustomers[0].id);
        }
        // Notify modals that records were detached
        if (item?.repair_id) {
          window.dispatchEvent(new CustomEvent('repairDetached', { detail: { repairId: item.repair_id } }));
        }
        if (item?.appraisal_id) {
          window.dispatchEvent(new CustomEvent('appraisalDetached', { detail: { appraisalId: item.appraisal_id } }));
        }
        if (item?.custom_job_id) {
          window.dispatchEvent(new CustomEvent('customJobDetached', { detail: { customJobId: item.custom_job_id } }));
        }
      } catch (err) {
        console.error('Error removing line item:', err);
      }
    }
    setLineItems(lineItems.filter(i => i.id !== id));
    setIsDirty(true);
  };

  const handleEditClick = (item, field = 'description') => {
    setEditingItemId(item.id);
    setEditValues({
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      taxable: item.taxable !== undefined ? item.taxable : true,
      taxIncluded: item.taxIncluded !== undefined ? item.taxIncluded : false,
      returned: item.returned !== undefined ? item.returned : false,
      title: item.title || '',
      sku: item.sku || ''
    });
    setFocusedField(field);
  };

  // Open the repair/custom-job edit modal for a source line item.
  // Fetches the full parent record first so the modal initializes in edit mode.
  const handleOpenSourceEditor = async (item) => {
    try {
      if (item.repair_id) {
        const res = await fetch(`${API_URL}/repairs/${item.repair_id}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to load repair record');
        const data = await res.json();
        setEditSourceRecord({ type: 'repair', record: data.repair });
        setShowRepairModal(true);
      } else if (item.custom_job_id) {
        const res = await fetch(`${API_URL}/custom-jobs/${item.custom_job_id}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to load custom job record');
        const data = await res.json();
        setEditSourceRecord({ type: 'customJob', record: data.custom_job });
        setShowCustomJobModal(true);
      }
    } catch (err) {
      console.error('Error opening source editor:', err);
      setError(err.message || 'Failed to open repair/custom job editor');
    }
  };

  // Update a source line item in place from its saved parent record and persist.
  const syncSourceLineItem = async (lineItem, parentRecord) => {
    const rawPhotos = parentRecord.photos;
    let photos = Array.isArray(rawPhotos) ? rawPhotos : [];
    if (typeof rawPhotos === 'string') {
      try { photos = JSON.parse(rawPhotos); } catch { photos = []; }
    }
    if (!Array.isArray(photos)) photos = [];

    const descText = parentRecord.description || parentRecord.title || '';
    const updatedItem = {
      ...lineItem,
      title: parentRecord.title || lineItem.title || '',
      description: descText.length > 100 ? descText.substring(0, 100) + '...' : descText,
      quantity: lineItem.quantity || 1,
      unitPrice: parseFloat(parentRecord.price) || 0,
      taxable: parentRecord.taxable !== undefined ? parentRecord.taxable : true,
      taxIncluded: (parentRecord.tax_included !== undefined ? parentRecord.tax_included : parentRecord.taxIncluded) || false,
      photos
    };

    const updatedLineItems = lineItems.map(li => li.id === lineItem.id ? updatedItem : li);
    setLineItems(updatedLineItems);
    setIsDirty(true);
    await handleSaveTransaction(updatedLineItems, false);
  };

  const handleSaveEdit = (id) => {
    const updatedItems = lineItems.map(item =>
      item.id === id
        ? {
            ...item,
            description: editValues.description,
            quantity: parseInt(editValues.quantity) || 1,
            unitPrice: parseFloat(editValues.unitPrice) || 0,
            taxable: editValues.taxable !== undefined ? editValues.taxable : item.taxable,
            taxIncluded: editValues.taxIncluded !== undefined ? editValues.taxIncluded : (item.taxIncluded || false),
            returned: editValues.returned !== undefined ? editValues.returned : (item.returned || false),
            title: editValues.title ? editValues.title.substring(0, 100) : '',
            sku: editValues.sku ? editValues.sku.substring(0, 20) : ''
          }
        : item
    );
    setLineItems(updatedItems);
    setEditingItemId(null);
    setEditValues({ description: '', quantity: 1, unitPrice: 0 });
    setFocusedField(null);
    setIsDirty(true);
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditValues({ description: '', quantity: 1, unitPrice: 0 });
    setFocusedField(null);
    // Canceling an edit doesn't clear dirty since the edit might have been the only change
  };

  useEffect(() => {
    if (editingItemId && focusedField) {
      const inputRef = {
        description: descriptionInputRef,
        quantity: quantityInputRef,
        unitPrice: unitPriceInputRef
      }[focusedField];
      if (inputRef?.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }
  }, [editingItemId, focusedField]);

  // Get effective line items - merge editValues for the currently editing item
  const getEffectiveItems = () => {
    if (!editingItemId) return lineItems;
    return lineItems.map(item =>
      item.id === editingItemId
        ? {
            ...item,
            description: editValues.description,
            quantity: parseInt(editValues.quantity) || 1,
            unitPrice: parseFloat(editValues.unitPrice) || 0,
            taxable: editValues.taxable !== undefined ? editValues.taxable : item.taxable,
            taxIncluded: editValues.taxIncluded !== undefined ? editValues.taxIncluded : (item.taxIncluded || false),
            returned: editValues.returned !== undefined ? editValues.returned : (item.returned || false)
          }
        : item
    );
  };

  const calculateSubtotal = () => {
    const items = getEffectiveItems();
    return items.filter(item => !item.returned).reduce((sum, item) => {
      const qty = parseInt(item.quantity) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      const lineTotal = qty * price;
      // When tax is included, subtotal = pre-tax amount (price / 1.065)
      // Round per line to 2 decimals to match DB trigger behavior
      if (item.taxIncluded && item.taxable) {
        return sum + Math.round((lineTotal / 1.065) * 100) / 100;
      }
      return sum + Math.round(lineTotal * 100) / 100;
    }, 0);
  };

  const calculateTaxableAmount = () => {
    const items = getEffectiveItems();
    return items
      .filter(item => item.taxable && !item.taxIncluded && !item.returned)
      .reduce((sum, item) => sum + ((parseInt(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)), 0);
  };

  const calculateTax = () => {
    const items = getEffectiveItems();
    // Calculate tax per line item, rounding each to 2 decimals to match DB trigger
    return items.filter(item => !item.returned).reduce((sum, item) => {
      const qty = parseInt(item.quantity) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      const lineTotal = qty * price;
      let lineTax = 0;
      if (item.taxIncluded && item.taxable) {
        // Tax-included items: tax = price - (price / 1.065)
        lineTax = lineTotal - lineTotal / 1.065;
      } else if (item.taxable) {
        // Normal taxable items: tax = price * 0.065
        lineTax = lineTotal * 0.065;
      }
      return sum + Math.round(lineTax * 100) / 100;
    }, 0);
  };

  const calculateTotal = () => {
    return Math.round((calculateSubtotal() + calculateTax()) * 100) / 100;
  };

  // Payment helpers
  const calculatePaymentsSum = () => {
    return payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  };

  const calculateRemainingBalance = () => {
    return Math.round((calculateTotal() - calculatePaymentsSum()) * 100) / 100;
  };

  const calculateRefundDue = () => {
    // Refund due = absolute value of the negative remaining balance
    // Only shows when customer has overpaid (balance < 0)
    const balance = calculateRemainingBalance();
    return balance < 0 ? Math.round(Math.abs(balance) * 100) / 100 : 0;
  };

  const handleAddPayment = () => {
    setEditingPayment(null);
    setShowPaymentModal(true);
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async () => {
    // Refresh payments from server
    const txId = editingTransaction?.id || createdTransactionIdRef.current;
    if (txId) {
      try {
        const response = await fetch(`${API_URL}/transactions/${txId}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setPayments(data.payments || []);
        }
      } catch (err) {
        console.error('Error refreshing payments:', err);
      }
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment? The transaction status may be updated.')) return;
    try {
      const response = await fetch(`${API_URL}/transactions/payments/${paymentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        // Refresh payments from server
        const refreshTxId = editingTransaction?.id || createdTransactionIdRef.current;
        if (refreshTxId) {
          const refreshResponse = await fetch(`${API_URL}/transactions/${refreshTxId}`, {
            credentials: 'include'
          });
          if (refreshResponse.ok) {
            const data = await refreshResponse.json();
            setPayments(data.payments || []);
          }
        }
      }
    } catch (err) {
      console.error('Error deleting payment:', err);
    }
  };

  const formatPaymentDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    // payment_date is a calendar date stored as UTC midnight (the picker sends
    // `new Date('YYYY-MM-DD')` = midnight UTC). Render in UTC so the browser's
    // local timezone doesn't shift it back a day (e.g. EDT = UTC-4).
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  // Handle repair creation from TransactionModal - adds repair as a line item and saves transaction
  const handleRepairSuccess = async (repair) => {
    setShowRepairModal(false);
    const wasEditingSource = !!editSourceRecord;
    setEditSourceRecord(null);
    try {
      if (wasEditingSource) {
        // Edit mode: the repair already exists as a line item — sync it in place
        const li = lineItems.find(item => item.repair_id === repair.id);
        if (li) {
          await syncSourceLineItem(li, repair);
        } else {
          setError('Repair line item not found in transaction');
        }
        if (selectedCustomers.length > 0) {
          fetchUnattachedRepairs(selectedCustomers[0].id);
        }
        return;
      }
      // Parse photos if stored as JSON string by backend
      let repairPhotos = repair.photos;
      if (typeof repairPhotos === 'string') {
        try { repairPhotos = JSON.parse(repairPhotos); } catch { repairPhotos = []; }
      }
      const newItem = {
        id: Date.now(),
        description: (repair.description || repair.title || 'Repair').length > 100
          ? (repair.description || repair.title || 'Repair').substring(0, 100) + '...'
          : (repair.description || repair.title || 'Repair'),
        title: repair.title || '',
        quantity: 1,
        unitPrice: parseFloat(repair.price) || 0,
        taxable: repair.taxable !== undefined ? repair.taxable : true,
        taxIncluded: (repair.taxIncluded || repair.tax_included) || false,
        photo_url: null,
        photos: Array.isArray(repairPhotos) ? repairPhotos : [],
        sku: '',
        repair_id: repair.id
      };
      const updatedLineItems = [...lineItems, newItem];
      setLineItems(updatedLineItems);
      setIsDirty(true);
      // Immediately save the transaction to attach the repair in the database
      await handleSaveTransaction(updatedLineItems, false);
      // Refetch unattached repairs
      if (selectedCustomers.length > 0) {
        fetchUnattachedRepairs(selectedCustomers[0].id);
      }
    } catch (err) {
      console.error('Error adding repair to transaction:', err);
      setError(err.message || 'Failed to attach repair to transaction');
    }
  };

  // Handle custom job creation from TransactionModal - adds custom job as a line item and saves transaction
  const handleCustomJobSuccess = async (customJob) => {
    setShowCustomJobModal(false);
    const wasEditingSource = !!editSourceRecord;
    setEditSourceRecord(null);
    try {
      if (wasEditingSource) {
        // Edit mode: the custom job already exists as a line item — sync it in place
        const li = lineItems.find(item => item.custom_job_id === customJob.id);
        if (li) {
          await syncSourceLineItem(li, customJob);
        } else {
          setError('Custom job line item not found in transaction');
        }
        if (selectedCustomers.length > 0) {
          fetchUnattachedCustomJobs(selectedCustomers[0].id);
        }
        return;
      }
      // Parse photos if stored as JSON string by backend
      let customJobPhotos = customJob.photos;
      if (typeof customJobPhotos === 'string') {
        try { customJobPhotos = JSON.parse(customJobPhotos); } catch { customJobPhotos = []; }
      }
      const newItem = {
        id: Date.now(),
        description: (customJob.description || customJob.title || 'Custom Job').length > 100
          ? (customJob.description || customJob.title || 'Custom Job').substring(0, 100) + '...'
          : (customJob.description || customJob.title || 'Custom Job'),
        title: customJob.title || '',
        quantity: 1,
        unitPrice: parseFloat(customJob.price) || 0,
        taxable: customJob.taxable !== undefined ? customJob.taxable : true,
        taxIncluded: (customJob.taxIncluded || customJob.tax_included) || false,
        photo_url: null,
        photos: Array.isArray(customJobPhotos) ? customJobPhotos : [],
        sku: '',
        custom_job_id: customJob.id
      };
      const updatedLineItems = [...lineItems, newItem];
      setLineItems(updatedLineItems);
      setIsDirty(true);
      // Immediately save the transaction to attach the custom job in the database
      await handleSaveTransaction(updatedLineItems, false);
      // Refetch unattached custom jobs
      if (selectedCustomers.length > 0) {
        fetchUnattachedCustomJobs(selectedCustomers[0].id);
      }
    } catch (err) {
      console.error('Error adding custom job to transaction:', err);
      setError(err.message || 'Failed to attach custom job to transaction');
    }
  };

  // Handle appraisal creation from TransactionModal - adds appraisal as a line item and saves transaction
  const handleAppraisalSuccess = async (appraisal) => {
    setShowAppraisalModal(false);
    try {
      // Parse photos if stored as JSON string by backend
      let appraisalPhotos = appraisal.photos;
      if (typeof appraisalPhotos === 'string') {
        try { appraisalPhotos = JSON.parse(appraisalPhotos); } catch { appraisalPhotos = []; }
      }
      const newItem = {
        id: Date.now(),
        description: (appraisal.description || 'Appraisal').length > 100
          ? (appraisal.description || 'Appraisal').substring(0, 100) + '...'
          : (appraisal.description || 'Appraisal'),
        title: 'Appraisal',
        quantity: 1,
        unitPrice: parseFloat(appraisal.unit_price || appraisal.value) || 0,
        taxable: appraisal.taxable !== undefined ? appraisal.taxable : true,
        taxIncluded: (appraisal.taxIncluded || appraisal.tax_included) || false,
        photo_url: null,
        photos: Array.isArray(appraisalPhotos) ? appraisalPhotos : [],
        sku: '',
        appraisal_id: appraisal.id
      };
      const updatedLineItems = [...lineItems, newItem];
      setLineItems(updatedLineItems);
      setIsDirty(true);
      // Immediately save the transaction to attach the appraisal in the database
      await handleSaveTransaction(updatedLineItems, false);
      // Refetch unattached appraisals
      if (selectedCustomers.length > 0) {
        fetchUnattachedAppraisals(selectedCustomers[0].id);
      }
    } catch (err) {
      console.error('Error adding appraisal to transaction:', err);
      setError(err.message || 'Failed to attach appraisal to transaction');
    }
  };

  // Save transaction handler - defined before JSX return for proper hoisting
  async function handleSaveTransaction(overrideLineItems, shouldClose = false) {
    if (selectedCustomers.length === 0) {
      setError('No customer selected');
      return;
    }
    
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      const itemsToUse = Array.isArray(overrideLineItems) ? overrideLineItems : lineItems;
      const payload = {
        customer_id: selectedCustomers[0].id,
        status: 'draft',
        is_closed: isClosed,
        is_layaway: isLayaway,
        notes: null,
        shop_id: shopId || null,
        line_items: itemsToUse.map(item => ({
          // If the ID is a timestamp (from local creation), don't send it to the backend
          // so it treats it as a new item.
          id: typeof item.id === 'number' && item.id > 1000000000000 ? undefined : item.id,
          title: item.title || null,
          sku: item.sku || null,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          taxable: item.taxable,
          tax_included: item.taxIncluded || false,
          photo_url: item.photo_url || null,
          photos: item.photos || [],
          repair_id: item.repair_id || null,
          appraisal_id: item.appraisal_id || null,
          custom_job_id: item.custom_job_id || null,
          returned: item.returned || false
        }))
      };
      
      const isEditing = editingTransaction || createdTransactionIdRef.current;
      const url = isEditing
        ? `${API_URL}/transactions/${editingTransaction ? editingTransaction.id : createdTransactionIdRef.current}`
        : `${API_URL}/transactions`;
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${editingTransaction ? 'update' : 'save'} transaction`);
      }
      
      const data = await response.json();
      console.log('Transaction saved:', data);
      setSuccess(true);
      
      // Clear dirty flag after successful save
      setIsDirty(false);
      
      // Always sync line items from server response so local state has real DB IDs
      if (data.line_items) {
        const prevItems = lineItems;
        setLineItems(data.line_items.map(item => {
          const prev = prevItems.find(p => p.id === item.id);
          return {
            ...item,
            unitPrice: item.unit_price,
            taxIncluded: item.tax_included || false,
            returned: item.returned || false,
            photo_url: item.photo_url || null,
            photos: typeof item.photos === 'string' ? JSON.parse(item.photos) : (Array.isArray(item.photos) ? item.photos : []),
            repair_id: item.repair_id || null,
            appraisal_id: item.appraisal_id || null,
            custom_job_id: item.custom_job_id || null,
            // PUT response line items are not joined to parents — keep prior enriched values until refresh
            repair_instructions: item.repair_instructions !== undefined ? item.repair_instructions : (prev?.repair_instructions || ''),
            repair_due_type: item.repair_due_type !== undefined ? item.repair_due_type : (prev?.repair_due_type || null),
            repair_due_date: item.repair_due_date !== undefined ? item.repair_due_date : (prev?.repair_due_date || null),
            repair_created_by_name: item.repair_created_by_name !== undefined ? item.repair_created_by_name : (prev?.repair_created_by_name || null),
            cj_instructions: item.cj_instructions !== undefined ? item.cj_instructions : (prev?.cj_instructions || ''),
            cj_due_type: item.cj_due_type !== undefined ? item.cj_due_type : (prev?.cj_due_type || null),
            cj_due_date: item.cj_due_date !== undefined ? item.cj_due_date : (prev?.cj_due_date || null),
            cj_created_by_name: item.cj_created_by_name !== undefined ? item.cj_created_by_name : (prev?.cj_created_by_name || null)
          };
        }));
      }
      
      // Track created transaction ID so subsequent saves use PUT instead of POST
      // Use ref to avoid race condition with React async state updates
      if (!editingTransaction && !createdTransactionIdRef.current && data.id) {
        createdTransactionIdRef.current = data.id;
      }
      
      // Refetch full transaction data to sync payments and other server-side computed fields
      if (data.id) {
        try {
          const refreshResponse = await fetch(`${API_URL}/transactions/${data.id}`, {
            credentials: 'include'
          });
          if (refreshResponse.ok) {
            const freshData = await refreshResponse.json();
            setPayments(freshData.payments || []);
            // Sync computed totals from server
            if (freshData.line_items) {
              setLineItems(freshData.line_items.map(item => ({
                ...item,
                unitPrice: item.unit_price,
                taxIncluded: item.tax_included || false,
                returned: item.returned || false,
                photo_url: item.photo_url || null,
                photos: typeof item.photos === 'string' ? JSON.parse(item.photos) : (Array.isArray(item.photos) ? item.photos : []),
                repair_id: item.repair_id || null,
                appraisal_id: item.appraisal_id || null,
                custom_job_id: item.custom_job_id || null,
                repair_instructions: item.repair_instructions || '',
                repair_due_type: item.repair_due_type || null,
                repair_due_date: item.repair_due_date || null,
                repair_created_by_name: item.repair_created_by_name || null,
                cj_instructions: item.cj_instructions || '',
                cj_due_type: item.cj_due_type || null,
                cj_due_date: item.cj_due_date || null,
                cj_created_by_name: item.cj_created_by_name || null
              })));
            }
          }
        } catch (refreshErr) {
          console.error('Error refreshing transaction data:', refreshErr);
        }
      }
      
      // Only call onSuccess and close if shouldClose is true (default is now false)
      // Note: onSuccess in parent (handleTransactionModalSuccess) closes the modal,
      // so we only call it when shouldClose is true
      if (shouldClose) {
        if (onSuccess) onSuccess(data);
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    } catch (err) {
      console.error('Error saving transaction:', err);
      setError(err.message || 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            {!customer && !editingTransaction?.customer ? (
              // Search bar shown only when no customer is specified and not editing existing transaction
              <div className="flex-1 max-w-md">
                <label className="block text-xs font-medium text-gray-700 mb-1">Customer</label>
                <div className="relative">
                  <div
                    className={`min-h-[42px] px-3 py-2 border rounded-md text-sm flex items-center flex-wrap gap-2 transition-colors border-gray-300 focus-within:ring-2 focus-within:ring-indigo-500`}
                  >
                    {selectedCustomers.map((customer) => (
                      <span
                        key={customer.id}
                        className="inline-flex items-center px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium"
                      >
                        {`${customer.first_name} ${customer.last_name}`.trim()}
                        <button
                          onClick={() => handleRemoveCustomer(customer.id)}
                          className="ml-1 text-indigo-600 hover:text-indigo-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      ref={searchInputRef}
                      type="text"
                      autoComplete="off"
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                      onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                      disabled={selectedCustomers.length > 0}
                      placeholder={selectedCustomers.length === 0 ? "Search customers..." : "Customer selected"}
                      className={`flex-1 outline-none min-w-0 ${selectedCustomers.length > 0 ? 'cursor-not-allowed text-gray-400' : ''}`}
                    />
                    {searchQuery.length > 0 && selectedCustomers.length === 0 && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setSearchResults([]);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  
                  {/* Search Results Dropdown */}
                  {showSearchResults && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      <div
                        onClick={handleCreateNewCustomer}
                        className="px-4 py-2 text-sm cursor-pointer hover:bg-indigo-50 flex items-center gap-2 text-indigo-600 border-b border-gray-200"
                      >
                        <span className="text-lg">+</span>
                        <span>Create New Customer</span>
                      </div>
                      {searching ? (
                        <div className="px-4 py-2 text-sm text-gray-500">Searching...</div>
                      ) : searchResults.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-500">No customers found</div>
                      ) : (
                        searchResults.map((cust) => (
                          <div
                            key={cust.id}
                            onClick={() => handleCustomerSelect(cust)}
                            className="px-4 py-2 text-sm cursor-pointer hover:bg-indigo-50"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex flex-col">
                                <span className="text-gray-900">
                                  {`${cust.first_name} ${cust.last_name}`.trim()}
                                </span>
                                {(cust.city || cust.state) && (
                                  <span className="text-xs text-gray-400 mt-0.5">
                                    {[cust.city, cust.state].filter(Boolean).join(', ')}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-col text-right gap-0.5">
                                {cust.mobile_phone && (
                                  <span className="text-xs text-gray-500">Mobile: {cust.mobile_phone}</span>
                                )}
                                {cust.home_phone && (
                                  <span className="text-xs text-gray-500">Home: {cust.home_phone}</span>
                                )}
                                {cust.work_phone && (
                                  <span className="text-xs text-gray-500">Work: {cust.work_phone}</span>
                                )}
                                {!cust.mobile_phone && !cust.home_phone && !cust.work_phone && (
                                  <span className="text-xs text-gray-500">{cust.email || 'No contact'}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {selectedCustomers.length > 0 && (
                  <div className="mt-2 flex flex-col gap-2 text-xs text-gray-600">
                    {selectedCustomers.map(customer => (
                      <div key={customer.id} className="flex flex-col gap-1">
                        <span className="font-medium text-gray-900">
                          {`${customer.first_name} ${customer.last_name}`.trim()}
                        </span>
                        <div className="flex flex-col gap-0.5 pl-2">
                          {customer.mobile_phone && <span>Mobile: {customer.mobile_phone}</span>}
                          {customer.home_phone && <span>Home: {customer.home_phone}</span>}
                          {customer.work_phone && <span>Work: {customer.work_phone}</span>}
                          {!customer.mobile_phone && !customer.home_phone && !customer.work_phone && <span>{customer.email || 'No contact'}</span>}
                          {(customer.address_line1 || customer.city || customer.state || customer.postal_code) && (
                            <span className="text-gray-400 mt-1">
                              {[customer.address_line1, customer.address_line2, customer.city, customer.state, customer.postal_code].filter(Boolean).join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Non-editable header when customer is specified or editing existing transaction
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Date: <span className="font-medium">{new Date().toLocaleDateString()}</span></label>
                <div className="flex flex-col gap-1">
                  {(() => {
                    const cust = customer || editingTransaction?.customer;
                    return (
                      <>
                        <span className="text-sm font-medium text-gray-900">
                          {`${cust.first_name} ${cust.last_name}`.trim()}
                        </span>
                        <div className="flex flex-col gap-0.5 text-xs text-gray-600">
                          {cust.mobile_phone && <span>Mobile: {cust.mobile_phone}</span>}
                          {cust.home_phone && <span>Home: {cust.home_phone}</span>}
                          {cust.work_phone && <span>Work: {cust.work_phone}</span>}
                          {!cust.mobile_phone && !cust.home_phone && !cust.work_phone && cust.email && <span>{cust.email}</span>}
                          {(cust.address_line1 || cust.city || cust.state) && (
                            <span className="text-gray-400 mt-1">
                              {[cust.address_line1, cust.address_line2, cust.city, cust.state, cust.postal_code].filter(Boolean).join(', ')}
                            </span>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-4">
              {/* Shop ID Field */}
              <div>
                {isAdmin && editingTransaction && !isClosed ? (
                  <select
                    value={shopId}
                    onChange={(e) => setShopId(e.target.value)}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">No Shop</option>
                    {shops.map((shop) => (
                      <option key={shop.id} value={shop.shop_id}>{shop.shop_id}</option>
                    ))}
                  </select>
                ) : (
                  shopId && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {shopId}
                    </span>
                  )
                )}
              </div>
              {(editingTransaction || createdTransactionIdRef.current) && (
                              <button
                                type="button"
                                onClick={() => {
                                  const txId = editingTransaction?.id || createdTransactionIdRef.current;
                                  if (txId) {
                                    window.open(`${API_URL}/transactions/${txId}/pdf`, '_blank');
                                  }
                                }}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                                title="Print Receipt"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                                Print
                              </button>
                            )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-200px)]">

          {/* Unattached Repairs Section (Ghost Entries) - only show when customer is selected */}
          {selectedCustomers.length > 0 && unattachedRepairs.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowRepairsSection(!showRepairsSection)}
                className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-amber-600 text-lg">⚠</span>
                  <span className="text-sm font-medium text-amber-800">
                    {unattachedRepairs.length} unattached repair{unattachedRepairs.length > 1 ? 's' : ''} available to add
                  </span>
                </div>
                <span className="text-amber-600 text-sm">{showRepairsSection ? '▴ Hide' : '▾ Show'}</span>
              </button>

              {showRepairsSection && (
                <div className="mt-2 border border-amber-200 rounded-md p-4 bg-amber-50">
                  <p className="text-xs text-amber-700 mb-3">
                    Select repairs to add as line items. Each selected repair will be attached to this transaction.
                  </p>
                  <div className="space-y-2">
                    {unattachedRepairs.map(repair => {
                      const isSelected = selectedRepairs.includes(repair.id);
                      const statusColors = {
                        'new': 'bg-gray-100 text-gray-800',
                        'in-progress': 'bg-blue-100 text-blue-800',
                        'completed': 'bg-green-100 text-green-800',
                        'on-hold': 'bg-yellow-100 text-yellow-800'
                      };
                      return (
                        <label
                          key={repair.id}
                          className={`flex items-center justify-between p-3 rounded-md border cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-indigo-50 border-indigo-300'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSelected) {
                                  setSelectedRepairs(selectedRepairs.filter(id => id !== repair.id));
                                } else {
                                  setSelectedRepairs([...selectedRepairs, repair.id]);
                                }
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">{repair.title}</span>
                                {repair.jewelry_type && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{repair.jewelry_type}</span>
                                )}
                                <span className={`text-xs px-1.5 py-0.5 rounded ${statusColors[repair.ticket_status] || 'bg-gray-100 text-gray-800'}`}>
                                  {repair.ticket_status}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {repair.customer_name}
                                {repair.description && ` • ${repair.description.substring(0, 50)}${repair.description.length > 50 ? '...' : ''}`}
                              </div>
                            </div>
                          </div>
                          <div className="text-sm font-medium text-gray-900 ml-3">
                            ${parseFloat(repair.price || 0).toFixed(2)}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {selectedRepairs.length > 0 && (
                    <button
                      onClick={async () => {
                        const newItems = selectedRepairs.map(repairId => {
                          const repair = unattachedRepairs.find(r => r.id === repairId);
                          if (repair) {
                            return {
                              id: Date.now() + repairId,
                              description: ((repair.description || repair.title) || '').length > 100
                                ? ((repair.description || repair.title) || '').substring(0, 100) + '...'
                                : (repair.description || repair.title),
                              title: repair.title,
                              quantity: 1,
                              unitPrice: parseFloat(repair.price) || 0,
                              taxable: repair.taxable !== undefined ? repair.taxable : true,
                              photo_url: null,
                              photos: Array.isArray(repair.photos) ? repair.photos : [],
                              sku: '',
                              repair_id: repair.id
                            };
                          }
                          return null;
                        }).filter(item => item !== null);
                        
                        const updatedLineItems = [...lineItems, ...newItems];
                        setLineItems(updatedLineItems);
                        setSelectedRepairs([]);
                        
                        // Immediately save the transaction to attach repairs in the database
                        // Pass false as second arg to prevent modal from closing
                        await handleSaveTransaction(updatedLineItems, false);
                        
                        // Refetch unattached repairs to remove the ones just attached
                        if (selectedCustomers.length > 0) {
                          fetchUnattachedRepairs(selectedCustomers[0].id);
                        }
                      }}
                      className="mt-3 w-full px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      Attach {selectedRepairs.length} Repair{selectedRepairs.length > 1 ? 's' : ''} & Save Transaction
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Unattached Custom Jobs Section (Ghost Entries) - only show when customer is selected */}
          {selectedCustomers.length > 0 && unattachedCustomJobs.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowCustomJobsSection(!showCustomJobsSection)}
                className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-amber-600 text-lg">⚠</span>
                  <span className="text-sm font-medium text-amber-800">
                    {unattachedCustomJobs.length} unattached custom job{unattachedCustomJobs.length > 1 ? 's' : ''} available to add
                  </span>
                </div>
                <span className="text-amber-600 text-sm">{showCustomJobsSection ? '▴ Hide' : '▾ Show'}</span>
              </button>

              {showCustomJobsSection && (
                <div className="mt-2 border border-amber-200 rounded-md p-4 bg-amber-50">
                  <p className="text-xs text-amber-700 mb-3">
                    Select custom jobs to add as line items. Each selected custom job will be attached to this transaction.
                  </p>
                  <div className="space-y-2">
                    {unattachedCustomJobs.map(customJob => {
                      const isSelected = selectedCustomJobs.includes(customJob.id);
                      const statusColors = {
                        'new': 'bg-gray-100 text-gray-800',
                        'in-progress': 'bg-blue-100 text-blue-800',
                        'completed': 'bg-green-100 text-green-800',
                        'on-hold': 'bg-yellow-100 text-yellow-800'
                      };
                      return (
                        <label
                          key={customJob.id}
                          className={`flex items-center justify-between p-3 rounded-md border cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-indigo-50 border-indigo-300'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSelected) {
                                  setSelectedCustomJobs(selectedCustomJobs.filter(id => id !== customJob.id));
                                } else {
                                  setSelectedCustomJobs([...selectedCustomJobs, customJob.id]);
                                }
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">{customJob.title}</span>
                                {customJob.jewelry_type && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{customJob.jewelry_type}</span>
                                )}
                                <span className={`text-xs px-1.5 py-0.5 rounded ${statusColors[customJob.ticket_status] || 'bg-gray-100 text-gray-800'}`}>
                                  {customJob.ticket_status}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {customJob.customer_name}
                                {customJob.description && ` • ${customJob.description.substring(0, 50)}${customJob.description.length > 50 ? '...' : ''}`}
                              </div>
                            </div>
                          </div>
                          <div className="text-sm font-medium text-gray-900 ml-3">
                            ${parseFloat(customJob.price || 0).toFixed(2)}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {selectedCustomJobs.length > 0 && (
                    <button
                      onClick={async () => {
                        const newItems = selectedCustomJobs.map(customJobId => {
                          const customJob = unattachedCustomJobs.find(cj => cj.id === customJobId);
                          if (customJob) {
                            return {
                              id: Date.now() + customJobId,
                              description: ((customJob.description || customJob.title) || '').length > 100
                                ? ((customJob.description || customJob.title) || '').substring(0, 100) + '...'
                                : (customJob.description || customJob.title),
                              title: customJob.title,
                              quantity: 1,
                              unitPrice: parseFloat(customJob.price) || 0,
                              taxable: customJob.taxable !== undefined ? customJob.taxable : true,
                              photo_url: null,
                              photos: Array.isArray(customJob.photos) ? customJob.photos : [],
                              sku: '',
                              custom_job_id: customJob.id
                            };
                          }
                          return null;
                        }).filter(item => item !== null);
                        
                        const updatedLineItems = [...lineItems, ...newItems];
                        setLineItems(updatedLineItems);
                        setSelectedCustomJobs([]);
                        
                        // Immediately save the transaction to attach custom jobs in the database
                        await handleSaveTransaction(updatedLineItems, false);
                        
                        // Refetch unattached custom jobs to remove the ones just attached
                        if (selectedCustomers.length > 0) {
                          fetchUnattachedCustomJobs(selectedCustomers[0].id);
                        }
                      }}
                      className="mt-3 w-full px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      Attach {selectedCustomJobs.length} Custom Job{selectedCustomJobs.length > 1 ? 's' : ''} & Save Transaction
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Unattached Appraisals Section (Ghost Entries) - only show when customer is selected */}
          {selectedCustomers.length > 0 && unattachedAppraisals.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowAppraisalsSection(!showAppraisalsSection)}
                className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-amber-600 text-lg">⚠</span>
                  <span className="text-sm font-medium text-amber-800">
                    {unattachedAppraisals.length} unattached appraisal{unattachedAppraisals.length > 1 ? 's' : ''} available to add
                  </span>
                </div>
                <span className="text-amber-600 text-sm">{showAppraisalsSection ? '▴ Hide' : '▾ Show'}</span>
              </button>

              {showAppraisalsSection && (
                <div className="mt-2 border border-amber-200 rounded-md p-4 bg-amber-50">
                  <p className="text-xs text-amber-700 mb-3">
                    Select appraisals to add as line items. Each selected appraisal will be attached to this transaction.
                  </p>
                  <div className="space-y-2">
                    {unattachedAppraisals.map(appraisal => {
                      const isSelected = selectedAppraisals.includes(appraisal.id);
                      return (
                        <label
                          key={appraisal.id}
                          className={`flex items-center justify-between p-3 rounded-md border cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-indigo-50 border-indigo-300'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSelected) {
                                  setSelectedAppraisals(selectedAppraisals.filter(id => id !== appraisal.id));
                                } else {
                                  setSelectedAppraisals([...selectedAppraisals, appraisal.id]);
                                }
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">{appraisal.description || 'Appraisal'}</span>
                                {appraisal.characteristics && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{appraisal.characteristics}</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {appraisal.customer_name}
                              </div>
                            </div>
                          </div>
                          <div className="text-sm font-medium text-gray-900 ml-3">
                            ${parseFloat(appraisal.unit_price || appraisal.value || 0).toFixed(2)}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {selectedAppraisals.length > 0 && (
                    <button
                      onClick={async () => {
                        const newItems = selectedAppraisals.map(appraisalId => {
                          const appraisal = unattachedAppraisals.find(a => a.id === appraisalId);
                          if (appraisal) {
                            return {
                              id: Date.now() + appraisalId,
                              description: (appraisal.description || 'Appraisal').length > 100
                                ? (appraisal.description || 'Appraisal').substring(0, 100) + '...'
                                : (appraisal.description || 'Appraisal'),
                              title: 'Appraisal',
                              quantity: 1,
                              unitPrice: parseFloat(appraisal.unit_price || appraisal.value) || 0,
                              taxable: appraisal.taxable !== undefined ? appraisal.taxable : true,
                              photo_url: null,
                              photos: Array.isArray(appraisal.photos) ? appraisal.photos : [],
                              sku: '',
                              appraisal_id: appraisal.id
                            };
                          }
                          return null;
                        }).filter(item => item !== null);
                        
                        const updatedLineItems = [...lineItems, ...newItems];
                        setLineItems(updatedLineItems);
                        setSelectedAppraisals([]);
                        
                        // Immediately save the transaction to attach appraisals in the database
                        // Pass false as second arg to prevent modal from closing
                        await handleSaveTransaction(updatedLineItems, false);
                        
                        // Refetch unattached appraisals to remove the ones just attached
                        if (selectedCustomers.length > 0) {
                          fetchUnattachedAppraisals(selectedCustomers[0].id);
                        }
                      }}
                      className="mt-3 w-full px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      Attach {selectedAppraisals.length} Appraisal{selectedAppraisals.length > 1 ? 's' : ''} & Save Transaction
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Line Items Section */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Line Items</h3>
              <div className="flex items-center gap-3">
                {selectedCustomers.length > 0 && (
                  <>
                    <button
                      onClick={() => setShowRepairModal(true)}
                      className="flex items-center gap-1 px-2.5 py-1 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 transition-colors"
                    >
                      +Repair
                    </button>
                    <button
                      onClick={() => setShowCustomJobModal(true)}
                      className="flex items-center gap-1 px-2.5 py-1 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 transition-colors"
                    >
                      +Custom Job
                    </button>
                    <button
                      onClick={() => setShowAppraisalModal(true)}
                      className="flex items-center gap-1 px-2.5 py-1 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors"
                    >
                      +Appraisal
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowLineItemModal(true)}
                  className="flex items-center gap-1 px-2.5 py-1 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                >
                  +Add Item
                </button>
              </div>
            </div>
            
            {/* Line Items Table */}
            <div className="border rounded-md mb-4">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-20">Photo</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32">Title</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">SKU</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Unit Price</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Total</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16">Tax</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16">Tax Incl</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16">Return</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-12"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lineItems.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="px-3 py-4 text-center text-sm text-gray-500">
                        No line items added yet
                      </td>
                    </tr>
                  ) : (
                    lineItems.map((item, idx) => {
                      const isEditing = editingItemId === item.id;
                      // Repair / custom job items: description cell shows two distinct rows
                      // and clicking the row opens the parent record's edit modal.
                      const isSourceItem = !!(item.repair_id || item.custom_job_id);
                      return (
                        <tr key={item.id}>
                          <td className="px-3 py-2 text-center">
                            {(() => {
                              const photos = Array.isArray(item.photos) && item.photos.length > 0 ? item.photos : [];
                              const primaryPhoto = photos.length > 0 ? photos[0] : null;
                              // Fallback to legacy photo_url
                              const displayPhoto = primaryPhoto?.url || item.photo_url;
                              const hasPhotos = photos.length > 0 || item.photo_url;

                              return (
                                <div className="relative inline-block">
                                  {displayPhoto ? (
                                    <img
                                      src={displayPhoto.startsWith('http') ? displayPhoto : displayPhoto}
                                      alt="Item"
                                      className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-75"
                                      onClick={() => {
                                        const idx = lineItems.findIndex(li => li.id === item.id);
                                        setPhotoEditorItemIndex(idx);
                                        setShowPhotoEditor(true);
                                      }}
                                    />
                                  ) : (
                                    <button
                                      onClick={() => {
                                        const idx = lineItems.findIndex(li => li.id === item.id);
                                        setPhotoEditorItemIndex(idx);
                                        setShowPhotoEditor(true);
                                      }}
                                      className="w-12 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-indigo-500 hover:text-indigo-500 text-xs"
                                      title="Add Photo"
                                    >
                                      +
                                    </button>
                                  )}
                                  {photos.length > 1 && (
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-600 text-white text-[10px] rounded-full flex items-center justify-center font-medium">
                                      {photos.length}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td
                            className="px-3 py-2 cursor-pointer hover:bg-gray-50"
                            onClick={() => !isEditing && (isSourceItem ? handleOpenSourceEditor(item) : handleEditClick(item, 'title'))}
                            title={isSourceItem ? 'Edit in repair/custom job modal' : undefined}
                          >
                            {isEditing ? (
                              <input
                                type="text"
                                autoComplete="off"
                                value={editValues.title || ''}
                                onChange={(e) => setEditValues({ ...editValues, title: e.target.value.substring(0, 100) })}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit(item.id)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Title"
                                maxLength={100}
                              />
                            ) : isSourceItem ? (
                              <span className="text-sm text-gray-900 truncate block" title={item.title || ''}>
                                {item.title || '-'}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-900 truncate block" title={item.title || ''}>
                                {item.title || '-'}
                              </span>
                            )}
                          </td>
                          <td
                            className="px-3 py-2 cursor-pointer hover:bg-gray-50"
                            onClick={() => !isEditing && (isSourceItem ? handleOpenSourceEditor(item) : handleEditClick(item, 'sku'))}
                            title={isSourceItem ? 'Edit in repair/custom job modal' : undefined}
                          >
                            {isEditing ? (
                              <input
                                type="text"
                                autoComplete="off"
                                value={editValues.sku || ''}
                                onChange={(e) => setEditValues({ ...editValues, sku: e.target.value.substring(0, 20) })}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit(item.id)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="SKU"
                                maxLength={20}
                              />
                            ) : isSourceItem ? (
                              <div className="leading-tight">
                                <span className="text-sm text-gray-900 font-medium">
                                  {item.repair_id
                                    ? formatTicketId('R', item.repair_id, item.repair_created_by_name)
                                    : formatTicketId('C', item.custom_job_id, item.cj_created_by_name)}
                                </span>
                                {(item.repair_id ? item.repair_created_by_name : item.cj_created_by_name) && (
                                  <div className="text-xs text-gray-500">
                                    ({item.repair_id ? item.repair_created_by_name : item.cj_created_by_name})
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-900 truncate block" title={item.sku || ''}>
                                {item.sku || '-'}
                              </span>
                            )}
                          </td>
                          <td
                            className="px-3 py-2 cursor-pointer hover:bg-gray-50"
                            onClick={() => !isEditing && (isSourceItem ? handleOpenSourceEditor(item) : handleEditClick(item, 'description'))}
                            title={isSourceItem ? 'Edit in repair/custom job modal' : undefined}
                          >
                            {isEditing ? (
                              <input
                                ref={descriptionInputRef}
                                type="text"
                                autoComplete="off"
                                value={editValues.description}
                                onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit(item.id)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Description"
                              />
                            ) : isSourceItem ? (
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                                item.repair_id ? 'bg-amber-100 text-amber-800' : 'bg-teal-100 text-teal-800'
                              }`}>
                                {item.repair_id ? 'Repair' : 'Custom Job'}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-900">
                                {item.description}
                              </span>
                            )}
                          </td>
                          <td
                            className={isSourceItem ? 'px-3 py-2' : 'px-3 py-2 cursor-pointer hover:bg-gray-50'}
                            onClick={isSourceItem ? undefined : (() => !isEditing && handleEditClick(item, 'quantity'))}
                            title={isSourceItem ? 'Edit in repair/custom job modal' : undefined}
                          >
                            {isEditing ? (
                              <input
                                ref={quantityInputRef}
                                type="number"
                                value={editValues.quantity}
                                onChange={(e) => setEditValues({ ...editValues, quantity: e.target.value })}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit(item.id)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                min="1"
                              />
                            ) : (
                              <span className="text-sm text-gray-900">
                                {item.quantity}
                              </span>
                            )}
                          </td>
                          <td
                            className={isSourceItem ? 'px-3 py-2' : 'px-3 py-2 cursor-pointer hover:bg-gray-50'}
                            onClick={isSourceItem ? undefined : (() => !isEditing && handleEditClick(item, 'unitPrice'))}
                            title={isSourceItem ? 'Edit in repair/custom job modal' : undefined}
                          >
                            {isEditing ? (
                              <input
                                ref={unitPriceInputRef}
                                type="number"
                                value={editValues.unitPrice}
                                onChange={(e) => setEditValues({ ...editValues, unitPrice: e.target.value })}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit(item.id)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                min="0"
                                step="0.01"
                              />
                            ) : (
                              <span className="text-sm text-gray-900">
                                ${parseFloat(item.unitPrice || 0).toFixed(2)}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {isEditing
                              ? `$${((parseInt(editValues.quantity) || 0) * (parseFloat(editValues.unitPrice) || 0)).toFixed(2)}`
                              : `$${((parseInt(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)).toFixed(2)}`}
                          </td>
                          <td className="px-3 py-2 text-center relative" title={isSourceItem ? 'Edit in repair/custom job modal' : undefined}>
                            {isEditing ? (
                                <input
                                  type="checkbox"
                                  checked={editValues.taxable !== undefined ? editValues.taxable : true}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newChecked = !(editValues.taxable !== undefined ? editValues.taxable : true);
                                    console.log('taxable onClick', { itemId: item.id, newChecked });
                                    setEditValues({ ...editValues, taxable: newChecked });
                                  }}
                                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                                />
                            ) : isSourceItem ? (
                              <span
                                className={`inline-flex items-center justify-center w-5 h-5 rounded ${
                                  item.taxable
                                    ? 'bg-indigo-600 text-white'
                                    : 'border-2 border-gray-300'
                                }`}
                                title={item.taxable ? 'Taxable (edit in repair/custom job modal)' : 'Non-taxable (edit in repair/custom job modal)'}
                              >
                                {item.taxable && (
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                            ) : (
                              <span
                                className={`inline-flex items-center justify-center w-5 h-5 rounded ${
                                  item.taxable
                                    ? 'bg-indigo-600 text-white'
                                    : 'border-2 border-gray-300'
                                }`}
                                title={item.taxable ? 'Taxable' : 'Non-taxable'}
                              >
                                {item.taxable && (
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center relative">
                            {isEditing ? (
                                <input
                                  type="checkbox"
                                  checked={editValues.taxIncluded !== undefined ? editValues.taxIncluded : false}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newChecked = !(editValues.taxIncluded !== undefined ? editValues.taxIncluded : false);
                                    setEditValues({ ...editValues, taxIncluded: newChecked });
                                  }}
                                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                                />
                            ) : (
                              <span
                                className={`inline-flex items-center justify-center w-5 h-5 rounded ${
                                  item.taxIncluded
                                    ? 'bg-purple-600 text-white'
                                    : 'border-2 border-gray-300'
                                }`}
                                title={item.taxIncluded ? 'Tax Included' : 'Tax Not Included'}
                              >
                                {item.taxIncluded && (
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                            )}
                          </td>
                          {/* Return toggle cell */}
                          <td className="px-3 py-2 text-center">
                            {isEditing ? (
                              <input
                                type="checkbox"
                                checked={editValues.returned !== undefined ? editValues.returned : false}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newChecked = !(editValues.returned !== undefined ? editValues.returned : false);
                                  setEditValues({ ...editValues, returned: newChecked });
                                }}
                                className="w-4 h-4 text-red-600 rounded focus:ring-red-500 cursor-pointer"
                              />
                            ) : (
                              <span
                                className={`inline-flex items-center justify-center w-5 h-5 rounded ${
                                  item.returned
                                    ? 'bg-red-600 text-white'
                                    : 'border-2 border-gray-300'
                                }`}
                                title={item.returned ? 'Returned' : 'Not returned'}
                              >
                                {item.returned && (
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {isEditing ? (
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => {
                                    handleSaveEdit(item.id)
                                  }}
                                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-xl font-bold"
                                  title="Save"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-xl font-bold"
                                  title="Cancel"
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleRemoveLineItem(item.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Delete"
                              >
                                ×
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Add Line Item Modal */}
            {showLineItemModal && (
              <div className="fixed inset-0 z-[60] flex items-center justify-center">
                <div className="fixed inset-0 bg-black/50" onClick={() => setShowLineItemModal(false)} />
                <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md z-[70]">
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900">Add Line Item</h2>
                    <button onClick={() => setShowLineItemModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        autoComplete="off"
                        value={currentItem.title}
                        onChange={(e) => setCurrentItem({ ...currentItem, title: e.target.value.substring(0, 100) })}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddLineItem()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Item title"
                        maxLength={100}
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                      <input
                        type="text"
                        autoComplete="off"
                        value={currentItem.sku}
                        onChange={(e) => setCurrentItem({ ...currentItem, sku: e.target.value.substring(0, 20) })}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddLineItem()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Item SKU"
                        maxLength={20}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <input
                        type="text"
                        autoComplete="off"
                        value={currentItem.description}
                        onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddLineItem()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Item description"
                      />
                    </div>
                    <div className="flex gap-3">
                      <div className="w-28">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Qty</label>
                        <input
                          type="number"
                          value={currentItem.quantity}
                          onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddLineItem()}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          min="1"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                        <input
                          type="number"
                          value={currentItem.unitPrice}
                          onChange={(e) => setCurrentItem({ ...currentItem, unitPrice: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddLineItem()}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={currentItem.taxable}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentItem({ ...currentItem, taxable: !currentItem.taxable });
                          }}
                          className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-700">Taxable</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={currentItem.taxIncluded || false}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentItem({ ...currentItem, taxIncluded: !currentItem.taxIncluded });
                          }}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-700">Tax Included</span>
                      </label>
                      <div>
                        <input
                          ref={photoUploadRef}
                          type="file"
                          id="photo-upload-modal"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setUploadingPhoto(true);
                              try {
                                // Phones often hand over .webp; convert to JPEG so only jpg/png are uploaded
                                const uploadFile = await toJpegOrPng(file);
                                const formData = new FormData();
                                formData.append('file', uploadFile);
                                const token = localStorage.getItem('token');
                                const res = await fetch(`${API_URL}/uploads?folder=transactions`, {
                                  method: 'POST',
                                  headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                                  credentials: 'include',
                                  body: formData
                                });
                                if (res.ok) {
                                  const data = await res.json();
                                  setCurrentItem({ ...currentItem, photo_url: data.url });
                                }
                              } catch (err) {
                                console.error('Error uploading photo:', err);
                              } finally {
                                setUploadingPhoto(false);
                                if (photoUploadRef.current) photoUploadRef.current.value = '';
                              }
                            }
                          }}
                          className="hidden"
                        />
                        <label
                          htmlFor="photo-upload-modal"
                          className="text-sm font-medium text-indigo-600 hover:text-indigo-700 cursor-pointer"
                        >
                          {uploadingPhoto ? 'Uploading...' : '+ Photo'}
                        </label>
                      </div>
                    </div>
                    {currentItem.photo_url && (
                      <div className="flex items-center gap-2">
                        <img src={currentItem.photo_url.startsWith('http') ? currentItem.photo_url : currentItem.photo_url} alt="Preview" className="w-8 h-8 object-cover rounded" />
                        <span className="text-xs text-gray-500">Photo attached</span>
                      </div>
                    )}
                  </div>
                  <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 rounded-b-lg">
                    <button
                      onClick={() => setShowLineItemModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        handleAddLineItem();
                        setShowLineItemModal(false);
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                    >
                      Add Item
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Totals Section */}
          <div className="border-t border-gray-200 pt-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium text-gray-900">${calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax (6.5%):</span>
                <span className="font-medium text-gray-900">${calculateTax().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                <span className="text-gray-900">Total:</span>
                <span className="text-indigo-600">${calculateTotal().toFixed(2)}</span>
              </div>
            </div>

            {/* Payments Section - show when editing existing transaction OR after creating a new one */}
            {(editingTransaction || createdTransactionIdRef.current) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">Payments</h3>
                  <button
                    onClick={handleAddPayment}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                  >
                    <span className="text-sm">+</span> Add Payment
                  </button>
                </div>

                {/* Payments List */}
                {payments.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {payments.map((payment) => (
                      <div
                        key={payment.id}
                        className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-2"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 whitespace-nowrap">
                            {payment.payment_type}
                          </span>
                          {parseFloat(payment.amount) < 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 whitespace-nowrap">
                              Refund
                            </span>
                          )}
                          {(() => {
                            const refId = payment.reference_number || payment.notes;
                            if (refId) {
                              return (
                                <span className="text-xs text-gray-500 whitespace-nowrap" title={refId}>
                                  ref id {refId}
                                </span>
                              );
                            }
                            return null;
                          })()}
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {formatPaymentDate(payment.payment_date)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span className={`font-medium whitespace-nowrap ${
                            parseFloat(payment.amount) < 0 ? 'text-red-600' : 'text-gray-900'
                          }`}>
                            {parseFloat(payment.amount) < 0 ? '-' : ''}${Math.abs(parseFloat(payment.amount)).toFixed(2)}
                          </span>
                          <button
                            onClick={() => handleEditPayment(payment)}
                            className="text-gray-400 hover:text-indigo-600 text-xs"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeletePayment(payment.id)}
                            className="text-gray-400 hover:text-red-600 text-xs"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Payment Summary */}
                <div className="space-y-1 pt-2 border-t border-gray-100">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Payments:</span>
                    <span className="font-medium text-green-600">-${calculatePaymentsSum().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-bold">
                    <span className="text-gray-900">Balance:</span>
                    <span className={calculateRemainingBalance() <= 0 ? 'text-green-600' : 'text-red-600'}>
                      ${calculateRemainingBalance().toFixed(2)}
                    </span>
                  </div>
                  {calculateRemainingBalance() <= 0 && calculateTotal() > 0 && (
                    <span className="text-xs text-green-600 font-medium">✓ Paid in Full</span>
                  )}
                  {calculateRemainingBalance() < 0 && calculateRefundDue() > 0 && (
                    <div className="flex justify-between text-sm font-bold pt-1 border-t border-red-100">
                      <span className="text-red-600">Refund Due:</span>
                      <span className="text-red-600">${calculateRefundDue().toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isLayaway}
                onChange={() => { setIsLayaway(!isLayaway); setIsDirty(true); }}
                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-700 cursor-pointer" onClick={() => { setIsLayaway(!isLayaway); setIsDirty(true); }}>Layaway</span>
            </label>
            <button
              onClick={async () => {
                if (!editingTransaction) return;
                const newClosed = !isClosed;
                setIsClosed(newClosed);
                setIsDirty(true);
                try {
                  const response = await fetch(`${API_URL}/transactions/${editingTransaction.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                      customer_id: selectedCustomers[0]?.id,
                      status: editingTransaction.status,
                      notes: null,
                      is_closed: newClosed,
                      is_layaway: isLayaway,
                      shop_id: shopId || null,
                      line_items: lineItems.map(item => ({
                        id: typeof item.id === 'number' && item.id > 1000000000000 ? undefined : item.id,
                        title: item.title || null,
                        sku: item.sku || null,
                        description: item.description,
                        quantity: item.quantity,
                        unit_price: item.unitPrice,
                        taxable: item.taxable,
                        tax_included: item.taxIncluded || false,
                        photo_url: item.photo_url || null,
                        photos: item.photos || []
                      }))
                    })
                  });
                  if (!response.ok) throw new Error('Failed to update transaction');
                  const data = await response.json();
                  if (onSuccess) onSuccess(data);
                } catch (err) {
                  setError(err.message);
                  setIsClosed(!newClosed);
                }
              }}
              disabled={!editingTransaction || saving}
              className={`px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                isClosed
                  ? 'text-amber-700 bg-amber-100 border border-amber-300 hover:bg-amber-200'
                  : 'text-green-700 bg-green-100 border border-green-300 hover:bg-green-200'
              }`}
            >
              {isClosed ? 'Open Transaction' : 'Close Transaction'}
            </button>
          </div>
          <div className="flex items-center gap-3">
            {error && <span className="text-sm text-red-600">{error}</span>}
            {success && <span className="text-sm text-green-600">Transaction saved! You can now add payments or continue editing.</span>}
            <button
              onClick={() => {
                if (isDirty) {
                  if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                    onClose();
                  }
                } else {
                  onClose();
                }
              }}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveTransaction}
              disabled={saving || lineItems.length === 0 || editingItemId !== null}
              title={editingItemId !== null ? 'Save or cancel the current line item edit first' : undefined}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : (editingTransaction ? 'Update Transaction' : 'Save Transaction')}
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Customer Modal for creating new customers */}
    <CustomerModal
      showModal={showCustomerModal}
      onClose={() => setShowCustomerModal(false)}
      onSuccess={handleCustomerCreated}
    />

    {/* Payment Modal */}
    <PaymentModal
      isOpen={showPaymentModal}
      onClose={() => { setShowPaymentModal(false); setEditingPayment(null); }}
      transactionId={editingTransaction?.id || createdTransactionIdRef.current}
      transactionTotal={calculateRemainingBalance()}
      onPaymentSuccess={handlePaymentSuccess}
      editingPayment={editingPayment}
      customerId={selectedCustomers[0]?.id}
    />

    {/* Photo Editor Modal */}
    <PhotoEditorModal
      isOpen={showPhotoEditor}
      onClose={() => setShowPhotoEditor(false)}
      photos={photoEditorItemIndex >= 0 ? (lineItems[photoEditorItemIndex]?.photos || []) : []}
      onSave={(newPhotos) => {
        if (photoEditorItemIndex >= 0 && photoEditorItemIndex < lineItems.length) {
          const updatedItems = [...lineItems];
          const item = { ...updatedItems[photoEditorItemIndex] };
          item.photos = newPhotos;
          // Update legacy photo_url to the first photo for backwards compatibility
          item.photo_url = newPhotos.length > 0 ? newPhotos[0].url : null;
          updatedItems[photoEditorItemIndex] = item;
          setLineItems(updatedItems);
        }
        setShowPhotoEditor(false);
      }}
    />

    {/* Repair Modal - higher z-index to appear over Transaction Modal */}
    {showRepairModal && (
      <div className="fixed inset-0 z-[80]">
        <RepairModal
          isOpen={showRepairModal}
          onClose={() => setShowRepairModal(false)}
          repair={editSourceRecord?.type === 'repair' ? editSourceRecord.record : undefined}
          customer={selectedCustomers[0] || null}
          onSuccess={handleRepairSuccess}
        />
      </div>
    )}

    {/* Appraisal Modal - higher z-index to appear over Transaction Modal */}
    {showAppraisalModal && (
      <div className="fixed inset-0 z-[80]">
        <AppraisalModal
          isOpen={showAppraisalModal}
          onClose={() => setShowAppraisalModal(false)}
          customer={selectedCustomers[0] || null}
          onSuccess={handleAppraisalSuccess}
          onGoToTransaction={() => {}}
        />
      </div>
    )}

    {/* Custom Job Modal - higher z-index to appear over Transaction Modal */}
    {showCustomJobModal && (
      <div className="fixed inset-0 z-[80]">
        <CustomJobModal
          isOpen={showCustomJobModal}
          onClose={() => setShowCustomJobModal(false)}
          customJob={editSourceRecord?.type === 'customJob' ? editSourceRecord.record : undefined}
          customer={selectedCustomers[0] || null}
          onSuccess={handleCustomJobSuccess}
        />
      </div>
    )}
    </>
  );

}
